import { useState } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Package, Mail, Clock, CheckCircle, Truck, XCircle } from 'lucide-react';
import { ordersAPI } from '../lib/api';
import { useApiQuery } from '../hooks/useApiQuery';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errorHandler';

const STATUS_CONFIG = {
  pending:   { label: 'Pending',   color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock,        dot: 'bg-yellow-400' },
  confirmed: { label: 'Confirmed', color: 'bg-blue-100 text-blue-800 border-blue-200',       icon: CheckCircle,  dot: 'bg-blue-400' },
  delivered: { label: 'Delivered', color: 'bg-green-100 text-green-800 border-green-100',    icon: Truck,        dot: 'bg-[#8acb95]' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700 border-red-100',          icon: XCircle,      dot: 'bg-red-400' },
};

const STATUS_STEPS = ['pending', 'confirmed', 'delivered'];

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, color: 'bg-slate-100 text-slate-700 border-slate-200', dot: 'bg-slate-400' };
  const Icon = cfg.icon || Clock;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${cfg.color}`}>
      <Icon className="w-3.5 h-3.5" />
      {cfg.label}
    </span>
  );
};

const StatusTimeline = ({ status }) => {
  const currentIndex = STATUS_STEPS.indexOf(status);
  const isCancelled  = status === 'cancelled';

  return (
    <div className="flex items-center gap-0 mt-4">
      {STATUS_STEPS.map((step, i) => {
        const cfg     = STATUS_CONFIG[step];
        const isActive = isCancelled ? false : i <= currentIndex;
        const isNow    = !isCancelled && i === currentIndex;
        return (
          <div key={step} className="flex items-center flex-1">
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all ${
                isActive
                  ? 'bg-[#8acb95] border-[#8acb95]'
                  : 'bg-white border-slate-200'
              } ${isNow ? 'ring-4 ring-[#8acb95]/20' : ''}`}>
                {isActive ? (
                  <CheckCircle className="w-4 h-4 text-white" />
                ) : (
                  <span className="w-2 h-2 rounded-full bg-slate-200" />
                )}
              </div>
              <p className={`text-[10px] font-medium mt-1.5 capitalize ${isActive ? 'text-[#6ab57e]' : 'text-slate-400'}`}>
                {step}
              </p>
            </div>
            {i < STATUS_STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mb-5 mx-1 transition-all ${
                !isCancelled && i < currentIndex ? 'bg-[#8acb95]' : 'bg-slate-200'
              }`} />
            )}
          </div>
        );
      })}
    </div>
  );
};

const MyOrdersPage = () => {
  const [email, setEmail]     = useState('');
  const [orders, setOrders]   = useState([]);
  const [searched, setSearched] = useState(false);
  const { refetch, isFetching: loading } = useApiQuery(
    ['allOrders'],
    () => ordersAPI.getAll(),
    { enabled: false }
  );

  const handleSearch = async (e) => {
    e.preventDefault();
    setSearched(true);
    try {
      const { data: response, isError } = await refetch();
      if (isError || !response) throw new Error('Fetch failed');
      const userOrders = response.data.filter(
        (o) => o.customer_email.toLowerCase() === email.toLowerCase()
      );
      setOrders(userOrders.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      if (userOrders.length === 0) toast.info('No orders found for this email address');
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to load orders');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc]">
      <Navbar />

      {/* Hero */}
      <section className="hero-gradient py-10 border-b border-slate-200" data-testid="my-orders-hero">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            My Orders
          </h1>
          <p className="text-slate-500">Track your orders by entering your email address</p>
        </div>
      </section>

      <section className="py-10 flex-1">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">

          {/* Email search form */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-8" >
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3" data-testid="my-orders-form">
              <div className="flex-1 relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  required
                  type="email"
                  placeholder="Enter your order email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="email-input"
                  className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#8acb95] focus:ring-2 focus:ring-[#8acb95]/10"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="view-orders-button"
                className="bg-[#8acb95] hover:bg-[#7bb885] disabled:bg-slate-300 text-white font-semibold px-6 py-3 rounded-xl text-sm transition-colors shadow-sm flex-shrink-0"
              >
                {loading ? 'Searching...' : 'View My Orders'}
              </button>
            </form>
          </div>

          {/* No results */}
          {searched && orders.length === 0 && (
            <div className="text-center py-16 bg-white rounded-2xl border border-slate-100" data-testid="no-orders-found">
              <Package className="w-14 h-14 text-slate-200 mx-auto mb-4" />
              <h3 className="font-bold text-slate-700 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>No orders found</h3>
              <p className="text-sm text-slate-400">No orders found for <strong>{email}</strong></p>
            </div>
          )}

          {/* Orders list */}
          {orders.length > 0 && (
            <div className="space-y-5" data-testid="orders-list">
              <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
                {orders.length} Order{orders.length > 1 ? 's' : ''} Found
              </p>

              {orders.map((order) => (
                <div
                  key={order.id}
                  className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden"
                  data-testid={`order-card-${order.id}`}
                >
                  {/* Header */}
                  <div className="flex flex-wrap items-start justify-between gap-4 p-5 border-b border-slate-100">
                    <div>
                      <h3 className="font-bold text-slate-800 text-base mb-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        #{order.order_number}
                      </h3>
                      <p className="text-xs text-slate-400">
                        {new Date(order.created_at).toLocaleDateString('en-US', {
                          year: 'numeric', month: 'long', day: 'numeric',
                          hour: '2-digit', minute: '2-digit',
                        })}
                      </p>
                    </div>
                    <StatusBadge status={order.status} />
                  </div>

                  {/* Status timeline */}
                  {order.status !== 'cancelled' && (
                    <div className="px-5 pb-2 border-b border-slate-100">
                      <StatusTimeline status={order.status} />
                    </div>
                  )}

                  {/* Items */}
                  <div className="p-5 border-b border-slate-100">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">
                      Items ({order.items.length})
                    </p>
                    <div className="space-y-2">
                      {order.items.map((item, index) => (
                        <div key={index} className="flex items-center justify-between gap-4 py-1.5">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate">{item.product_name}</p>
                            <p className="text-xs text-slate-400">SKU: {item.product_code}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-sm font-semibold text-slate-700">
                              KWD {item.price.toFixed(3)} × {item.quantity}
                            </p>
                            <p className="text-xs text-slate-400">
                              = KWD {(item.price * item.quantity).toFixed(3)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex flex-wrap items-center justify-between gap-4 p-5">
                    <div className="text-sm text-slate-500">
                      <p className="font-medium text-slate-700">{order.customer_name}</p>
                      <p>{order.customer_phone}</p>
                      <p className="text-xs mt-1 text-slate-400">{order.customer_address}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-400 mb-1">Total Amount</p>
                      <p className="text-2xl font-bold text-[#8acb95]">
                        KWD {order.total_amount.toFixed(3)}
                      </p>
                      <p className="text-xs text-slate-400">{order.payment_method}</p>
                    </div>
                  </div>

                  {order.notes && (
                    <div className="mx-5 mb-5 p-3 bg-amber-50 border border-amber-100 rounded-xl">
                      <p className="text-xs text-amber-700">
                        <span className="font-semibold">Note:</span> {order.notes}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MyOrdersPage;
